import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import CasaFacilReportButtons from '../components/CasaFacilReportButtons';
import { useCasaFacilProduct, casaFacilProductFromName } from '../lib/casaFacilProduct';
import { groupByReferenceMonth } from '../lib/referencePeriod';
import { 
  Upload as UploadIcon, 
  FileText, 
  X, 
  Play, 
  Trash2, 
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  BarChart3,
  Eye,
  Database,
  TrendingUp
} from 'lucide-react';
import api from '../services/api';

function Upload() {
  const { user } = useAuth();
  const product = useCasaFacilProduct();
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedFileData, setSelectedFileData] = useState(null);
  const [selectedFileStats, setSelectedFileStats] = useState(null);
  const [viewingData, setViewingData] = useState(false);

  console.log('🔄 Componente Upload renderizando...', { user });

  // Carregar arquivos existentes e estatísticas
  useEffect(() => {
    console.log('📥 useEffect executando...');
    loadFiles();
    loadStats();
  }, []);

  const loadFiles = async () => {
    try {
      console.log('📡 Carregando arquivos...');
      const response = await api.get('/upload/files');
      console.log('✅ Arquivos carregados:', response.data);
      setUploadedFiles(response.data.files);
    } catch (error) {
      console.error('❌ Erro ao carregar arquivos:', error);
    }
  };

  const loadStats = async () => {
    try {
      console.log('📊 Carregando estatísticas...');
      const response = await api.get('/upload/stats');
      console.log('✅ Estatísticas carregadas:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('❌ Erro ao carregar estatísticas:', error);
    }
  };

  // Drag and Drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(file => {
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/json',
        'text/plain'
      ];
      return allowedTypes.includes(file.type);
    });
    
    setFiles(prev => [...prev, ...validFiles]);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    
    files.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await api.post('/upload/files', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFiles([]);
      await loadFiles();
      await loadStats();
      
      // Mostrar mensagem detalhada sobre o upload
      const { summary, details } = response.data;
      let message = response.data.message;
      
      if (summary.duplicate_files > 0) {
        message += `\n\n📋 Resumo:`;
        message += `\n✅ ${summary.new_files} arquivo(s) novo(s) enviado(s)`;
        message += `\n⚠️ ${summary.duplicate_files} arquivo(s) duplicado(s) detectado(s)`;
        
        // Adicionar detalhes dos arquivos duplicados
        if (details.duplicate_files.length > 0) {
          message += `\n\n📁 Arquivos duplicados:`;
          details.duplicate_files.forEach(dup => {
            const date = new Date(dup.created_at).toLocaleDateString('pt-BR');
            const time = new Date(dup.created_at).toLocaleTimeString('pt-BR');
            message += `\n• ${dup.original_name} (já enviado em ${date} às ${time})`;
          });
        }
      }
      
      alert(message);
    } catch (error) {
      console.error('Erro no upload:', error);
      
      // Mostrar erro mais detalhado
      let errorMessage = 'Erro ao fazer upload dos arquivos';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const processFile = async (fileId) => {
    try {
      console.log('🚀 Iniciando processamento do arquivo:', fileId);
      console.log('📡 Chamando API:', `/upload/files/${fileId}/process`);
      
      const response = await api.post(`/upload/files/${fileId}/process`);
      console.log('✅ Resposta da API:', response.data);
      
      await loadFiles();
      alert('Processamento iniciado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao processar arquivo:', error);
      alert('Erro ao iniciar processamento');
    }
  };

  const reprocessFile = async (fileId) => {
    if (!window.confirm('Tem certeza que deseja reprocessar este arquivo? Os dados existentes serão removidos e inseridos novamente.')) {
      return;
    }

    try {
      console.log('🔄 Iniciando reprocessamento do arquivo:', fileId);
      console.log('📡 Chamando API:', `/upload/files/${fileId}/reprocess`);
      
      const response = await api.post(`/upload/files/${fileId}/reprocess`);
      console.log('✅ Resposta da API:', response.data);
      
      await loadFiles();
      alert('Reprocessamento iniciado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao reprocessar arquivo:', error);
      alert('Erro ao iniciar reprocessamento');
    }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Tem certeza que deseja excluir este arquivo?')) return;

    try {
      await api.delete(`/upload/files/${fileId}`);
      await loadFiles();
      await loadStats();
      alert('Arquivo removido com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar arquivo:', error);
      alert('Erro ao remover arquivo');
    }
  };

  const viewFileData = async (fileId) => {
    try {
      const [dataResponse, statsResponse] = await Promise.all([
        api.get(`/upload/files/${fileId}/data`),
        api.get(`/upload/files/${fileId}/stats`)
      ]);

      setSelectedFileData(dataResponse.data);
      setSelectedFileStats(statsResponse.data);
      setViewingData(true);
    } catch (error) {
      console.error('Erro ao carregar dados do arquivo:', error);
      alert('Erro ao carregar dados do arquivo');
    }
  };

  const closeDataView = () => {
    setViewingData(false);
    setSelectedFileData(null);
    setSelectedFileStats(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploaded':
        return <Clock className="h-4 w-4 text-primary-500" />;
      case 'processing':
        return <BarChart3 className="h-4 w-4 text-yellow-500" />;
      case 'completed':
      case 'processed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'duplicate':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'uploaded':
        return 'Enviado';
      case 'processing':
        return 'Processando';
      case 'completed':
      case 'processed':
        return 'Concluído';
      case 'error':
        return 'Erro';
      case 'duplicate':
        return 'Duplicado';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploaded':
        return 'text-primary-600 bg-primary-50';
      case 'processing':
        return 'text-yellow-600 bg-yellow-50';
      case 'completed':
      case 'processed':
        return 'text-green-600 bg-green-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'duplicate':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const visibleFiles = uploadedFiles.filter((file) => {
    if (!product) return true;
    return casaFacilProductFromName(file.original_name)?.id === product.id;
  });

  return (
    <Layout 
      headerTitle={product?.label || 'CASA FÁCIL RESOLVE'}
      headerSubtitle="Upload para integração com a MAXPAR"
    >

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upload Area */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload de Arquivos</h3>
            
            {/* Drag & Drop Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
                isDragging
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <UploadIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Arraste e solte arquivos aqui
              </p>
              <p className="text-sm text-gray-500 mb-4">
                ou clique para selecionar arquivos
              </p>
              <input
                type="file"
                multiple
                accept=".csv,.xlsx,.xls,.json,.txt"
                onChange={handleFileSelect}
                className="hidden"
                id="file-input"
              />
              <label
                htmlFor="file-input"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 cursor-pointer"
              >
                Selecionar Arquivos
              </label>
              <p className="text-xs text-gray-400 mt-2">
                CSV, Excel, JSON, TXT até 10MB cada
              </p>
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Arquivos Selecionados</h4>
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={uploadFiles}
                  disabled={uploading}
                  className="mt-4 w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Enviando...' : `Enviar ${files.length} arquivo(s)`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Estatísticas</h3>
            
            {stats ? (
              <div className="space-y-4">
                <div className="text-center p-4 bg-primary-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary-600">{stats.total_files || 0}</div>
                  <div className="text-sm text-primary-500">Total de Arquivos</div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-lg font-semibold text-green-600">{stats.completed_files || 0}</div>
                    <div className="text-xs text-green-500">Processados</div>
                  </div>
                  
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-lg font-semibold text-yellow-600">{stats.processing_files || 0}</div>
                    <div className="text-xs text-yellow-500">Processando</div>
                  </div>
                  
                  <div className="text-center p-3 bg-primary-50 rounded-lg">
                    <div className="text-lg font-semibold text-primary-600">{stats.uploaded_files || 0}</div>
                    <div className="text-xs text-primary-500">Pendentes</div>
                  </div>
                  
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-lg font-semibold text-red-600">{stats.error_files || 0}</div>
                    <div className="text-xs text-red-500">Erros</div>
                  </div>
                </div>
                
                {stats.total_size && (
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-600">
                      Tamanho Total: {formatFileSize(stats.total_size)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Carregando...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Uploaded Files */}
      <div className="mt-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Arquivos Enviados</h3>
          {product && (
            <p className="text-sm text-gray-600 mb-4">
              Arquivos de {product.label}. Em cada arquivo, além da integração com a MAXPAR, baixe o Excel de conferência e a fatura em PDF.
            </p>
          )}
          
          {visibleFiles.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">
                {uploadedFiles.length === 0 ? 'Nenhum arquivo enviado ainda' : `Nenhum arquivo de ${product?.label || 'CASA FÁCIL'}`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Arquivo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tamanho
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupByReferenceMonth(visibleFiles, (file) => file.original_name).map((group) => (
                    <React.Fragment key={group.key}>
                      <tr>
                        <td colSpan="5" className="px-6 py-2 bg-gray-100 text-sm font-semibold text-gray-800">
                          {group.label}
                        </td>
                      </tr>
                  {group.items.map((file) => (
                    <React.Fragment key={file.id}>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {file.original_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {file.filename}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatFileSize(file.file_size)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(file.status)}`}>
                            {getStatusIcon(file.status)}
                            <span className="ml-1 capitalize">{getStatusText(file.status)}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(file.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {file.status === 'uploaded' && (
                              <button
                                onClick={() => processFile(file.id)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                              >
                                <Play className="h-3 w-3 mr-1" />
                                Processar
                              </button>
                            )}
                            
                            {(file.status === 'completed' || file.status === 'processed') && (
                              <>
                                <button
                                  onClick={() => viewFileData(file.id)}
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Ver Dados
                                </button>
                                
                                <button
                                  onClick={() => reprocessFile(file.id)}
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-orange-600 hover:bg-orange-700"
                                >
                                  <BarChart3 className="h-3 w-3 mr-1" />
                                  Reprocessar
                                </button>
                              </>
                            )}
                            
                            <button
                              onClick={() => deleteFile(file.id)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Excluir
                            </button>
                            <CasaFacilReportButtons fileId={file.id} fileName={file.original_name} />
                          </div>
                        </td>
                      </tr>
                      {file.status === 'error' && file.error_message && (
                        <tr className="bg-red-50">
                          <td colSpan="6" className="px-6 py-2 text-sm text-red-600">
                            <div className="flex items-start">
                              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="font-medium">Erro:</span> {file.error_message}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Visualização de Dados */}
      {viewingData && selectedFileData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header da Modal */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    📊 Dados da Planilha: {selectedFileData.file?.original_name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Dados capturados da planilha "Vendas Guaibim"
                  </p>
                </div>
                <button
                  onClick={closeDataView}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Informações do Processamento */}
              {selectedFileData.processing_info && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-primary-800 mb-2">ℹ️ Informações do Processamento</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-primary-700">Planilha:</span>
                      <p className="text-primary-600">{selectedFileData.processing_info.target_sheet}</p>
                    </div>
                    <div>
                      <span className="font-medium text-primary-700">Total de Linhas:</span>
                      <p className="text-primary-600">{selectedFileData.captured_data?.rows?.length || 0}</p>
                    </div>
                    <div>
                      <span className="font-medium text-primary-700">Linhas Processadas:</span>
                      <p className="text-primary-600">{selectedFileData.processing_info.inserted_rows || 0}</p>
                    </div>
                    <div>
                      <span className="font-medium text-primary-700">Processado em:</span>
                      <p className="text-primary-600">
                        {new Date(selectedFileData.processing_info.processed_at).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Informações de Erro */}
              {selectedFileData.error_info && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-red-800 mb-2">❌ Erro no Processamento</h4>
                  <p className="text-red-600">{selectedFileData.error_info.error}</p>
                  <p className="text-red-500 text-sm mt-1">
                    Ocorreu em: {new Date(selectedFileData.error_info.error_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              )}

              {/* Dados Capturados da Planilha */}
              {selectedFileData.captured_data && selectedFileData.captured_data.rows.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">
                    📋 Dados Capturados da Planilha "Vendas Guaibim"
                  </h4>
                  


                  {/* Tabela de Dados */}
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Linha
                          </th>
                          {selectedFileData.captured_data.headers.map((header, index) => (
                            <th key={index} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedFileData.captured_data.rows.slice(0, 20).map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-xs text-gray-500 font-medium">
                              {rowIndex + 1}
                            </td>
                            {row.raw_row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="px-3 py-2 text-xs text-gray-900 max-w-xs truncate">
                                {cell !== null && cell !== undefined ? String(cell) : '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {selectedFileData.captured_data.rows.length > 20 && (
                    <p className="text-sm text-gray-500 mt-2 text-center">
                      Mostrando as primeiras 20 linhas de {selectedFileData.captured_data.rows.length} linhas capturadas
                    </p>
                  )}
                </div>
              )}

              {/* Dados do Banco */}
              {selectedFileData.database_data && selectedFileData.database_data.rows.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">
                    🗄️ Dados Inseridos no Banco
                  </h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(selectedFileData.database_data.rows[0]).map((key) => (
                            <th key={key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedFileData.database_data.rows.slice(0, 10).map((row, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            {Object.values(row).map((value, cellIndex) => (
                              <td key={cellIndex} className="px-3 py-2 text-xs text-gray-900 max-w-xs truncate">
                                {value !== null && value !== undefined ? String(value) : '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {selectedFileData.database_data.rows.length > 10 && (
                    <p className="text-sm text-gray-500 mt-2 text-center">
                      Mostrando as primeiras 10 linhas de {selectedFileData.database_data.rows.length} linhas no banco
                    </p>
                  )}
                </div>
              )}

              {/* Botão de Fechar */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeDataView}
                  className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Upload;
